export type Result<T, E> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly error: E }

export function ok<T>(value: T): { ok: true; value: T } {
  return { ok: true, value }
}

export function err<E>(error: E): { ok: false; error: E } {
  return { ok: false, error }
}

// The repo compiles with strictNullChecks off, which disables negative
// narrowing of discriminated unions: `.value`/`.error` are only reachable
// inside a positive guard branch, or through fold/mapOk below.
export function isOk<T, E>(
  result: Result<T, E>,
): result is { ok: true; value: T } {
  return result.ok
}

export function isErr<T, E>(
  result: Result<T, E>,
): result is { ok: false; error: E } {
  return !result.ok
}

export function fold<T, E, U>(
  result: Result<T, E>,
  onOk: (value: T) => U,
  onErr: (error: E) => U,
): U {
  // SAFETY: `ok` is the union discriminant; each cast is taken only on its
  // matching side of the branch.
  return result.ok
    ? onOk((result as { ok: true; value: T }).value)
    : onErr((result as { ok: false; error: E }).error)
}

export function mapOk<T, E, U>(
  result: Result<T, E>,
  transform: (value: T) => U,
): Result<U, E> {
  return fold<T, E, Result<U, E>>(
    result,
    value => ok(transform(value)),
    error => err(error),
  )
}

export type ChatError =
  | { readonly kind: 'network'; readonly message: string }
  | { readonly kind: 'http'; readonly status: number }
  | { readonly kind: 'decode'; readonly detail: string }

export function describeChatError(error: ChatError): string {
  switch (error.kind) {
    case 'network':
      return `network error: ${error.message}`
    case 'http':
      return `http ${error.status}`
    case 'decode':
      return `decode error: ${error.detail}`
  }
}
